import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CloudOff, FlaskConical, Loader2, RefreshCw, ServerCrash } from 'lucide-react'
import {
  type ConnectivityState,
  probeNow,
  subscribeConnectivity,
} from '@/lib/connectivity'
import { type CacheStatus, subscribeCacheStatus } from '@/lib/offline-cache'

function formatTime(timestamp: number): string {
  try {
    const saved = new Date(timestamp)
    const today = new Date()
    // A time alone is misleading once data is days old, which is exactly the
    // case an offline week creates. Show the day whenever it is not today.
    const sameDay =
      saved.getFullYear() === today.getFullYear() &&
      saved.getMonth() === today.getMonth() &&
      saved.getDate() === today.getDate()

    return sameDay
      ? saved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : saved.toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  } catch {
    return 'earlier'
  }
}

/** What went wrong, said plainly enough that nobody has to guess. */
function describe(reason: ConnectivityState['reason']): { title: string; detail: string } {
  switch (reason) {
    case 'browser':
      return {
        title: "You're offline",
        detail: 'This device has no internet connection.',
      }
    case 'server':
      return {
        title: "Can't reach StockWise",
        detail: "Your connection is working, but our servers aren't responding.",
      }
    case 'simulated':
      return {
        title: 'Offline mode (test)',
        detail: 'This device is pretending to be offline so the offline screens can be checked.',
      }
    default:
      return { title: "You're offline", detail: 'StockWise cannot be reached right now.' }
  }
}

/**
 * The one place that tells the shop whether StockWise is reachable.
 *
 * Deliberately loud: a thin grey line is easy to miss while ringing up a sale,
 * and being wrong about connectivity is how stock and money drift apart. The
 * bar also owns the browser tab title, so the state is visible when the app is
 * in a background tab or restored from the task switcher.
 *
 * It stays on screen for as long as the problem lasts — never a toast — and it
 * covers both "no internet" and "the server is unreachable", because for the
 * person at the till those are the same problem.
 */
export function OfflineDataBanner() {
  const [network, setNetwork] = useState<ConnectivityState>({ online: true, reason: null })
  const [cache, setCache] = useState<CacheStatus>({ usingCache: false, cachedAt: null })
  const [checking, setChecking] = useState(false)
  const queryClient = useQueryClient()
  const wasOnline = useRef(true)

  useEffect(() => {
    const unsubscribeNetwork = subscribeConnectivity(setNetwork)
    const unsubscribeCache = subscribeCacheStatus(setCache)
    return () => {
      unsubscribeNetwork()
      unsubscribeCache()
    }
  }, [])

  // Coming back should refetch by itself, so the bar clears without a reload.
  useEffect(() => {
    const restored = !wasOnline.current && network.online
    wasOnline.current = network.online
    if (restored) void queryClient.invalidateQueries()
  }, [network.online, queryClient])

  // Visible on the tab, which is what an operator glances at from across the
  // counter — the in-app bar is hidden the moment they switch tabs.
  useEffect(() => {
    if (network.online) return
    const previous = document.title
    document.title = `⚠ Offline — ${previous}`
    return () => {
      document.title = previous
    }
  }, [network.online])

  const handleRetry = async () => {
    setChecking(true)
    try {
      await probeNow()
    } finally {
      setChecking(false)
    }
  }

  // Online and serving fresh data: nothing to say.
  if (network.online && !cache.usingCache) return null

  const offline = !network.online
  const { title, detail } = describe(network.reason)

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      data-offline={offline ? 'true' : 'false'}
      data-offline-reason={network.reason ?? 'none'}
      className={
        offline
          ? 'border-b-2 border-red-900 bg-red-600 px-4 py-2.5 text-white'
          : 'border-b border-amber-300 bg-amber-100 px-4 py-2.5 text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100'
      }
    >
      <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-center text-sm">
        <span className="flex items-center gap-2 font-semibold">
          {network.reason === 'simulated' ? (
            <FlaskConical className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : network.reason === 'server' ? (
            <ServerCrash className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <CloudOff className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          {title}
        </span>

        <span className="opacity-95">{detail}</span>

        {cache.usingCache && cache.cachedAt && (
          <span className="rounded-md bg-black/20 px-2 py-0.5 text-xs font-medium">
            Showing saved data from {formatTime(cache.cachedAt)}
            {/* Old catalogue data still sells, but the price may have moved. */}
            {Date.now() - cache.cachedAt > 24 * 60 * 60 * 1000 && ' — prices may have changed'}
          </span>
        )}

        {network.reason === 'server' && (
          <button
            type="button"
            onClick={() => void handleRetry()}
            disabled={checking}
            className="flex items-center gap-1 rounded-md border border-white/50 px-2 py-0.5 text-xs font-medium hover:bg-white/15 disabled:opacity-60"
          >
            {checking ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
            )}
            {checking ? 'Checking…' : 'Check again'}
          </button>
        )}
      </div>

      {offline && (
        <p className="mx-auto mt-1 max-w-4xl text-center text-xs leading-relaxed opacity-90">
          Sales are saved on this device and sent automatically when the connection returns.
          Anything else must wait until you&apos;re back online.
        </p>
      )}
    </div>
  )
}

export default OfflineDataBanner
